import { useSelector } from "react-redux";
import { selectProfilePermissionsSelector } from "../../../../store/api/auth/selectors";
import ProfilePermissions from "../../../../utils/ProfilePermissions";
import { syslogForwardingColumn } from "../../../Shared/Table/Columns";
import { useStoreDispatch } from "../../../../store/hooks";
import { DIALOGOPENFOR } from "../../../../globals/globals";
import { setBasicDialogOpenFor, setConfirmDialog, setIsLoadingStatus, setOpenToast } from "../../../../store/helpers/helperReducers";
import { useEffect, useState } from "react";
import { ISyslogForward, ISyslogReceiver } from "../../../../store/api/admin/types";
import { IKeyValue } from "../../types";
import { selectedSyslogReceiverSelector } from "../../../../store/api/admin/selectors";
import { syslogReceiverDefault } from "../../helpers";
import {
  useCreateSyslogMutation,
  useDeleteSyslogMutation,
  useGetSyslogsQuery,
  useUpdateSyslogMutation,
} from "../../../../store/api/admin/adminApiSlice";
import { errorMessage } from "../../../../utils/helpers";
import { setSelectedSyslogReceiver } from "../../../../store/api/admin/reducers";
import { initialState } from "../../../../store/api/admin/initialState";
import { isIPv4, isIPv6, isServer, isValidHostName, isValidServerPort } from "../../../../utils/validations";

export const useSyslogForward = () => {
  const dispatch = useStoreDispatch();
  const permissions: ProfilePermissions | null = useSelector(selectProfilePermissionsSelector);

  const { data = [], refetch, isLoading } = useGetSyslogsQuery();
  const [formData, setFormData] = useState<ISyslogReceiver>(syslogReceiverDefault);
  const [errMsg, setErrMsg] = useState<IKeyValue>({});
  const [modelOpenMode, setModelOpenMode] = useState("Add");
  const selectedRow = useSelector(selectedSyslogReceiverSelector);
  const [createSyslog, syslogStatus] = useCreateSyslogMutation();
  const [updateSyslog, updateStatus] = useUpdateSyslogMutation();
  const [deleteSyslog, deleteStatus] = useDeleteSyslogMutation();

  useEffect(() => {
    dispatch(setIsLoadingStatus({ syslog: syslogStatus.isLoading || updateStatus.isLoading }));
  }, [dispatch, syslogStatus.isLoading, updateStatus.isLoading]);

  useEffect(() => {
    if (selectedRow?.hostname && selectedRow?.action === "edit") {
      let enabledListArray: string[] = [];
      if (!!selectedRow?.enabledList?.includes(",")) {
        enabledListArray = selectedRow.enabledList.split(",");
        enabledListArray = enabledListArray.map((element: string) => element.trim());
      } else {
        enabledListArray = !!selectedRow?.enabledList ? [selectedRow?.enabledList.trim()] : [];
      }
      setFormData({
        hostname: selectedRow?.hostname,
        port: selectedRow?.port,
        description: selectedRow?.description,
        enabled: selectedRow?.enabled,
        enableAuditTrail: enabledListArray.length > 0 && enabledListArray?.includes("Audit Trail"),
        enableSystemEvent: enabledListArray.length > 0 && enabledListArray?.includes("System Events"),
      });
      setModelOpenMode("Edit");
      setErrMsg({});
    }
  }, [selectedRow]);

  const addNewSysLogReceiver = () => {
    dispatch(setBasicDialogOpenFor(DIALOGOPENFOR.SYSLOGFORWARDING));
  };

  const submitHandler = async () => {
    try {
      let result: any;
      let enabledEvents: string = "NONE";
      if (formData?.enableAuditTrail) {
        enabledEvents = "Audit Trail";
      }
      if (formData?.enableSystemEvent) {
        enabledEvents = !!enabledEvents ? `${enabledEvents},System Events` : `System Events`;
      }
      const syslogPayload = {
        hostname: formData?.hostname || "",
        port: formData?.port || 0,
        description: formData?.description || "",
        enabled: formData?.enabled || false,
        enabledList: enabledEvents || "",
      };
      if (modelOpenMode === "Edit") {
        result = await updateSyslog({ payload: syslogPayload, objectKey: selectedRow.id.objectKey });
      } else {
        result = await createSyslog(syslogPayload);
      }

      if (result?.error) throw result?.error || syslogStatus?.isError;
      if (result.data?.status === 200) {
        setFormData(syslogReceiverDefault);
        setModelOpenMode("");
        dispatch(setBasicDialogOpenFor(undefined));
        dispatch(setSelectedSyslogReceiver(initialState.selectedSyslogReceiver));
        dispatch(setIsLoadingStatus({ syslog: false }));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The Syslog Receiver: ${formData?.hostname} has been ${modelOpenMode === "Edit" ? "updated" : "added"} successfully.`,
          })
        );
        refetch();
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const handleDeleteSyslogReceiver = async (param: any) => {
    if (!param.match("delete")) return;
    try {
      let result: any;

      result = await deleteSyslog(selectedRow.id.objectKey);
      if (result?.error) throw result?.error || deleteStatus?.isError;
      if (result.data?.status === 200) {
        dispatch(setConfirmDialog(undefined));
        dispatch(
          setOpenToast({
            openToast: true,
            toastType: "success",
            message: `The Syslog Receiver : ${selectedRow.hostname} has been deleted.`,
          })
        );
        dispatch(setSelectedSyslogReceiver(initialState.selectedSyslogReceiver));
        refetch();
      }
    } catch (error: any) {
      dispatch(setOpenToast({ openToast: true, toastType: "error", message: errorMessage(error) }));
    }
  };

  const onBlurHandler = (event: any) => {
    const { name, value } = event?.target;

    if (name === "hostname" && modelOpenMode !== "Edit") {
      if (!!value) {
        const isValidHost = isValidHostName(value) || isIPv4(value) || isIPv6(value) || isServer(value);
        if (!isValidHost) {
          setErrMsg({ [name]: `Host Name is invalid!` });
        }
        if (data.length > 0) {
          const isexist = data.some((item: ISyslogForward) => item.hostname.toLowerCase() === value.toLowerCase());
          if (isexist) {
            setErrMsg({ [name]: `Host Name is already in use!` });
          }
        }
      } else {
        setErrMsg({ [name]: `Host Name is required!` });
      }
    }
    if (name === "port") {
      if (!!value) {
        const isValidPort = isValidServerPort(value);
        if (!isValidPort) {
          setErrMsg({ [name]: `Port is invalid!` });
        }
      } else {
        setErrMsg({ [name]: `Port is required!` });
      }
    }
  };

  return {
    permissions,
    data,
    isLoading,
    syslogForwardingColumn,
    addNewSysLogReceiver,
    formData,
    setFormData,
    errMsg,
    setErrMsg,
    modelOpenMode,
    setModelOpenMode,
    handleDeleteSyslogReceiver,
    selectedRow,
    submitHandler,
    onBlurHandler,
    dispatch,
  };
};
